import {
    type RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

const STICK_TO_BOTTOM_THRESHOLD_PX = 32;

type UseTranscriptScrollOptions = {
    readonly currentRunNodeCount?: number;
    // Identifies content whose arrival returns the reader to the bottom even
    // after they scrolled up. `undefined` means the caller cannot tell yet.
    readonly returnToBottomKey?: string | null;
    readonly renderedTimelineEntryCount: number;
    readonly streamOverlayAssistantMessageCount?: number;
    readonly transcriptKey: string | null;
};

type UseTranscriptScrollReturn = {
    readonly transcriptContentRef: RefObject<HTMLDivElement | null>;
    readonly transcriptScrollAreaRef: RefObject<HTMLDivElement | null>;
    readonly stickTranscriptToBottomRef: RefObject<boolean>;
    readonly isTranscriptAtBottom: boolean;
    readonly isTranscriptScrollable: boolean;
    readonly scrollTranscriptToBottom: (behavior?: ScrollBehavior) => void;
    readonly handleTypewriterProgress: () => void;
};

type SmoothScrollState = {
    readonly lastScrollTop: number;
};

export function useTranscriptScroll({
    currentRunNodeCount = 0,
    renderedTimelineEntryCount,
    returnToBottomKey,
    streamOverlayAssistantMessageCount = 0,
    transcriptKey,
}: UseTranscriptScrollOptions): UseTranscriptScrollReturn {
    const transcriptContentRef = useRef<HTMLDivElement | null>(null);
    const transcriptScrollAreaRef = useRef<HTMLDivElement | null>(null);
    const stickTranscriptToBottomRef = useRef(true);
    const smoothScrollStateRef = useRef<SmoothScrollState | null>(null);
    const [isTranscriptAtBottom, setIsTranscriptAtBottom] = useState(true);
    const [isTranscriptScrollable, setIsTranscriptScrollable] = useState(false);
    const getTranscriptViewport = useCallback(
        () =>
            transcriptScrollAreaRef.current?.querySelector<HTMLElement>(
                '[data-slot="scroll-area-viewport"]',
            ) ?? null,
        [],
    );
    const scrollTranscriptToBottom = useCallback(
        (behavior: ScrollBehavior = 'auto') => {
            const viewport = getTranscriptViewport();
            if (!viewport) {
                return;
            }

            stickTranscriptToBottomRef.current = true;
            setIsTranscriptAtBottom(true);
            const top = viewport.scrollHeight;
            if (typeof viewport.scrollTo === 'function') {
                smoothScrollStateRef.current =
                    behavior === 'smooth'
                        ? { lastScrollTop: viewport.scrollTop }
                        : null;
                viewport.scrollTo({ top, behavior });
            } else {
                smoothScrollStateRef.current = null;
                viewport.scrollTop = top;
            }
        },
        [getTranscriptViewport],
    );
    const measureTranscriptScroll = useCallback(() => {
        const viewport = getTranscriptViewport();
        if (!viewport) {
            return;
        }

        const distanceToBottomPx =
            viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
        const isAtBottom = distanceToBottomPx <= STICK_TO_BOTTOM_THRESHOLD_PX;
        stickTranscriptToBottomRef.current = isAtBottom;
        setIsTranscriptAtBottom(isAtBottom);
        setIsTranscriptScrollable(
            viewport.scrollHeight > viewport.clientHeight,
        );
    }, [getTranscriptViewport]);
    const handleTypewriterProgress = useCallback(() => {
        if (stickTranscriptToBottomRef.current) {
            scrollTranscriptToBottom();
        }
    }, [scrollTranscriptToBottom]);
    // Scroll newly rendered history before passive measurement can mistake
    // asynchronous transcript growth for the reader scrolling up.
    useLayoutEffect(() => {
        if (stickTranscriptToBottomRef.current) {
            scrollTranscriptToBottom();
        }
    }, [
        currentRunNodeCount,
        renderedTimelineEntryCount,
        scrollTranscriptToBottom,
        streamOverlayAssistantMessageCount,
    ]);
    useEffect(() => {
        const viewport = getTranscriptViewport();
        if (!viewport) {
            return;
        }

        const handleScroll = () => {
            const smoothScrollState = smoothScrollStateRef.current;
            if (smoothScrollState === null) {
                measureTranscriptScroll();
                return;
            }

            if (viewport.scrollTop < smoothScrollState.lastScrollTop) {
                smoothScrollStateRef.current = null;
                measureTranscriptScroll();
                return;
            }

            smoothScrollStateRef.current = {
                lastScrollTop: viewport.scrollTop,
            };
            const distanceToBottomPx =
                viewport.scrollHeight -
                viewport.scrollTop -
                viewport.clientHeight;
            const isAtBottom =
                distanceToBottomPx <= STICK_TO_BOTTOM_THRESHOLD_PX;
            stickTranscriptToBottomRef.current = true;
            setIsTranscriptAtBottom(isAtBottom);
            setIsTranscriptScrollable(
                viewport.scrollHeight > viewport.clientHeight,
            );
            if (isAtBottom) {
                smoothScrollStateRef.current = null;
            }
        };
        const handleWheel = (event: WheelEvent) => {
            smoothScrollStateRef.current = null;
            if (event.deltaY < 0) {
                stickTranscriptToBottomRef.current = false;
                setIsTranscriptAtBottom(false);
            }
        };

        viewport.addEventListener('scroll', handleScroll, { passive: true });
        viewport.addEventListener('wheel', handleWheel, { passive: true });
        measureTranscriptScroll();
        return () => {
            viewport.removeEventListener('scroll', handleScroll);
            viewport.removeEventListener('wheel', handleWheel);
        };
    }, [
        currentRunNodeCount,
        getTranscriptViewport,
        measureTranscriptScroll,
        renderedTimelineEntryCount,
        streamOverlayAssistantMessageCount,
        transcriptKey,
    ]);
    useLayoutEffect(() => {
        stickTranscriptToBottomRef.current = true;
        setIsTranscriptAtBottom(true);
        scrollTranscriptToBottom();
    }, [scrollTranscriptToBottom, transcriptKey]);
    // Only a key change inside an already-rendered transcript is an arrival.
    // The first key after opening or hydrating a transcript is history, so it
    // becomes the baseline instead of moving the reader.
    const returnToBottomBaselineRef = useRef<{
        readonly hadEntries: boolean;
        readonly returnToBottomKey: string | null;
        readonly transcriptKey: string | null;
    } | null>(null);
    useLayoutEffect(() => {
        if (returnToBottomKey === undefined) {
            return;
        }
        const baseline = returnToBottomBaselineRef.current;
        returnToBottomBaselineRef.current = {
            hadEntries: renderedTimelineEntryCount > 0,
            returnToBottomKey,
            transcriptKey,
        };
        if (
            baseline !== null &&
            baseline.hadEntries &&
            baseline.transcriptKey === transcriptKey &&
            returnToBottomKey !== null &&
            returnToBottomKey !== baseline.returnToBottomKey
        ) {
            scrollTranscriptToBottom('smooth');
        }
    }, [
        renderedTimelineEntryCount,
        returnToBottomKey,
        scrollTranscriptToBottom,
        transcriptKey,
    ]);
    useEffect(() => {
        const contentElement = transcriptContentRef.current;
        if (!contentElement || typeof ResizeObserver === 'undefined') {
            return;
        }

        let animationFrameId: number | null = null;
        const scrollAfterResize = () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                if (stickTranscriptToBottomRef.current) {
                    scrollTranscriptToBottom();
                } else {
                    measureTranscriptScroll();
                }
            });
        };
        const resizeObserver = new ResizeObserver(scrollAfterResize);
        resizeObserver.observe(contentElement);
        scrollAfterResize();

        return () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
            resizeObserver.disconnect();
        };
    }, [
        measureTranscriptScroll,
        renderedTimelineEntryCount,
        scrollTranscriptToBottom,
        transcriptKey,
    ]);

    return {
        transcriptContentRef,
        transcriptScrollAreaRef,
        stickTranscriptToBottomRef,
        isTranscriptAtBottom,
        isTranscriptScrollable,
        scrollTranscriptToBottom,
        handleTypewriterProgress,
    };
}
